import { motion, useScroll, useSpring } from 'framer-motion';
import { useRef } from 'react';
import { GlassCard } from '../components/ui/GlassCard';
import { Briefcase, GraduationCap, Rocket, Terminal, Layers } from 'lucide-react';

const history = [
    {
        year: "2024 - PRESENT",
        title: "Senior Autonomous Architect",
        organization: "Lehana.in Labs",
        description: "Leading the development of hybrid infrastructure and Agentic AI ecosystems. Orchestrating multi-model pipelines and secure vector databases.",
        icon: <Rocket />,
        tech: ["Agentic AI", "Docker", "Traefik", "Elasticsearch"]
    },
    {
        year: "2023 - 2024",
        title: "Full Stack Systems Engineer",
        organization: "Distributed Systems Group",
        description: "Built high-performance web scrapers and real-time monitoring tools. Focused on scalability and automated failover protocols.",
        icon: <Layers />,
        tech: ["Vite", "FastAPI", "Python", "Redis"]
    },
    {
        year: "2021 - 2023",
        title: "Backend Specialist",
        organization: "Neural Core Tech",
        description: "Developed microservices for AI model deployment and secure data ingestion. Implemented JWT/OAuth2 and complex database migrations.",
        icon: <Briefcase />,
        tech: ["Node.js", "Express", "PostgreSQL", "AWS"]
    },
    {
        year: "2018 - 2021",
        title: "Software Research Fellow",
        organization: "Institute of Technology",
        description: "Conceptualized and built the early versions of decentralized identity systems and blockchain-based DNS protocols.",
        icon: <GraduationCap />,
        tech: ["Solidity", "React", "Go", "IPFS"]
    }
];

export function Timeline() {
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"]
    });

    const scaleY = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    return (
        <div className="pt-32 pb-48 px-6 md:px-12 max-w-7xl mx-auto min-h-screen">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-32 text-center"
            >
                <h1 className="text-6xl md:text-9xl font-black text-white tracking-tighter mb-8 italic">
                    SYSTEM <span className="text-emerald-500">TRAJECTORY</span>
                </h1>
                <p className="text-gray-400 font-mono tracking-[0.5em] uppercase text-xs md:text-sm">
                    A linear progression of architectural evolution.
                </p>
            </motion.div>

            <div ref={containerRef} className="relative">
                {/* Central Timeline Line */}
                <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-white/10 -translate-x-1/2 hidden md:block" />
                <motion.div
                    className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-emerald-500 -translate-x-1/2 origin-top hidden md:block z-10 shadow-[0_0_15px_rgba(16,185,129,0.5)]"
                    style={{ scaleY }}
                />

                <div className="space-y-32">
                    {history.map((item, idx) => (
                        <div key={idx} className={`flex flex-col md:flex-row items-center gap-8 ${idx % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                            {/* Content Side */}
                            <motion.div
                                initial={{ opacity: 0, x: idx % 2 === 0 ? 50 : -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ margin: "-100px" }}
                                className="w-full md:w-1/2"
                            >
                                <GlassCard className="p-8 border-white/5 hover:border-emerald-500/30 transition-all duration-500 bg-black/40">
                                    <div className="text-[10px] font-mono text-emerald-500 uppercase tracking-widest mb-2">{item.year}</div>
                                    <h2 className="text-2xl font-bold text-white mb-1 uppercase tracking-tight">{item.title}</h2>
                                    <div className="text-xs font-mono text-gray-500 mb-6">{item.organization}</div>

                                    <p className="text-gray-400 text-sm leading-relaxed mb-8 font-mono opacity-80">
                                        {item.description}
                                    </p>

                                    <div className="flex flex-wrap gap-2">
                                        {item.tech.map(t => (
                                            <span key={t} className="text-[9px] px-2 py-1 bg-emerald-500/5 text-emerald-500 border border-emerald-500/20 rounded font-mono uppercase">
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                </GlassCard>
                            </motion.div>

                            {/* Center Icon */}
                            <div className="relative z-20 flex items-center justify-center">
                                <motion.div
                                    whileInView={{ scale: [0, 1.2, 1], rotate: [0, 10, 0] }}
                                    viewport={{ margin: "-100px" }}
                                    className="w-12 h-12 rounded-full bg-tech-bg border-4 border-emerald-500 flex items-center justify-center text-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.3)]"
                                >
                                    {item.icon}
                                </motion.div>
                            </div>

                            {/* Empty Side (Spacer for Desktop) */}
                            <div className="hidden md:block w-1/2" />
                        </div>
                    ))}
                </div>
            </div>

            {/* Final Termination */}
            <div className="mt-48 flex flex-col items-center gap-8">
                <div className="w-12 h-12 rounded-full border border-emerald-500/20 flex items-center justify-center animate-pulse">
                    <Terminal className="text-emerald-500 w-6 h-6" />
                </div>
                <div className="text-center">
                    <h3 className="text-white font-bold tracking-widest uppercase">END_OF_SEQUENCE</h3>
                    <p className="text-[9px] font-mono text-gray-600 uppercase tracking-[1em] mt-2">New nodes pending...</p>
                </div>
            </div>
        </div>
    );
}
